import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

import { getAuthHeaders } from './authHeaders';

const usePrescriptionData = ({ show, editingNote }) => {
    const [treatments, setTreatments] = useState(['']);
    const [prescriptionNumber, setPrescriptionNumber] = useState('');
    const [diagnosis, setDiagnosis] = useState('');
    const [height, setHeight] = useState('');
    const [weight, setWeight] = useState('');

    const [loadingTreatments, setLoadingTreatments] = useState(false);
    const [uniqueTreatments, setUniqueTreatments] = useState([]);
    const [isLoadingUniqueTreatments, setIsLoadingUniqueTreatments] = useState(false);

    const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

    const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
    const treatmentRefs = useRef({});

    useEffect(() => {
        const fetchUniqueTreatments = async () => {
            try {
                setIsLoadingUniqueTreatments(true);
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}treatments/unique`,
                    {
                        headers: getAuthHeaders()
                    }
                );
                setUniqueTreatments(response.data || []);
            } catch (error) {
                console.error('Error fetching unique treatments:', error);
            } finally {
                setIsLoadingUniqueTreatments(false);
            }
        };

        if (show) {
            fetchUniqueTreatments();
        }
    }, [show]);

    useEffect(() => {
        const fetchPrescription = async () => {
            if (editingNote?.id && (editingNote.type === 'treatment' || editingNote.type === 'appointment')) {
                try {
                    setLoadingTreatments(true);
                    const patientId = editingNote.patient?.id || editingNote.patient_id;

                    if (!patientId) {
                        console.error('No patient ID found in editingNote:', editingNote);
                        return;
                    }

                    const noteResponse = await axios.get(
                        `${process.env.REACT_APP_API_URL}patients/${patientId}/notes/${editingNote.id}`,
                        {
                            headers: getAuthHeaders()
                        }
                    );

                    const noteData = noteResponse.data;
                    const prescription = noteData.prescriptions?.[0];

                    if (prescription) {
                        setPrescriptionNumber(prescription.number || '');
                        setDiagnosis(prescription.diagnosis || '');
                        setHeight(prescription.height || '');
                        setWeight(prescription.weight || '');
                        setTreatments(prescription.treatments?.map(t => t.value) || ['']);
                    } else {
                        setPrescriptionNumber('');
                        setDiagnosis('');
                        setHeight('');
                        setWeight('');
                        setTreatments(['']);
                    }
                } catch (error) {
                    console.error('Error fetching prescription:', error);
                    setPrescriptionNumber('');
                    setDiagnosis('');
                    setHeight('');
                    setWeight('');
                    setTreatments(['']);
                } finally {
                    setLoadingTreatments(false);
                }
            } else if (editingNote?.type !== 'treatment' && editingNote?.type !== 'appointment') {
                setPrescriptionNumber('');
                setDiagnosis('');
                setHeight('');
                setWeight('');
                setTreatments(['']);
            }
        };

        if (show) {
            fetchPrescription();
        }
    }, [editingNote, show]);

    const handleAddTreatment = () => {
        setTreatments(prev => [...prev, '']);
    };

    const handleRemoveTreatment = (index) => {
        setTreatments(prev => {
            if (prev.length <= 1) return prev;
            const next = [...prev];
            next.splice(index, 1);
            return next;
        });
    };

    const handleTreatmentChange = (index, value) => {
        setTreatments(prev => {
            const next = [...prev];
            next[index] = value;
            return next;
        });
    };

    return {
        treatments,
        setTreatments,
        prescriptionNumber,
        setPrescriptionNumber,
        diagnosis,
        setDiagnosis,
        height,
        setHeight,
        weight,
        setWeight,
        loadingTreatments,
        uniqueTreatments,
        isLoadingUniqueTreatments,
        showAdvancedOptions,
        setShowAdvancedOptions,
        openDropdownIndex,
        setOpenDropdownIndex,
        treatmentRefs,
        handleAddTreatment,
        handleRemoveTreatment,
        handleTreatmentChange,
    };
};

export default usePrescriptionData;
